/**
 * AdminReportSendTab — Batch monthly report generation + sending
 * Sprint 10: Fix T.cardBg→T.card, add PropTypes, dynamic period options,
 *            replace local fmtBRL with shared fmt.
 * Sprint 7: Automação — auto-seleciona novos clientes com e-mail,
 *           delivery history tracking, retry para falhas.
 */
import { useState, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useQuery } from "../shared/hooks.js";
import { fetchAllLatestSnapshots } from "../services/snapshots.js";
import { fetchAllClientsMeta } from "../services/clientMeta.js";
import { invokeSendMonthlyReport } from "../services/email.js";
import { supabase } from "../services/core.js";
import { logAudit } from "../supabaseClient.js";
import { SkeletonTable } from "../shared/Skeletons.jsx";
import { RADIUS } from "../shared/theme.js";
import { fmt, Badge } from "./adminUtils.jsx";
import ReportScheduleConfig from "./components/ReportScheduleConfig.jsx";

const STATUS = {
  IDLE: "idle",
  SENDING: "sending",
  DONE: "done",
  ERROR: "error",
};

/**
 * Generate the last 6 month period labels (e.g. "Mar/26", "Fev/26", …)
 * based on the current date.
 */
function buildPeriodOptions() {
  const months = [
    "Jan",
    "Fev",
    "Mar",
    "Abr",
    "Mai",
    "Jun",
    "Jul",
    "Ago",
    "Set",
    "Out",
    "Nov",
    "Dez",
  ];
  const now = new Date();
  const opts = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const m = months[d.getMonth()];
    const y = String(d.getFullYear()).slice(-2);
    opts.push(`${m}/${y}`);
  }
  return opts;
}

export default function AdminReportSendTab({ T }) {
  const periodOptions = useMemo(() => buildPeriodOptions(), []);
  const [selectedPeriod, setSelectedPeriod] = useState(periodOptions[0] || "");
  const [sendStatus, setSendStatus] = useState(STATUS.IDLE);
  const [results, setResults] = useState([]);
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [deliveryHistory, setDeliveryHistory] = useState([]);

  // Fetch clients
  const fetchClients = useCallback(async () => {
    const [snapshots, metas] = await Promise.all([
      fetchAllLatestSnapshots(),
      fetchAllClientsMeta(),
    ]);
    const { data: profiles } = await supabase.from("profiles").select("id, full_name, email");

    const profileMap = {};
    (profiles || []).forEach((p) => {
      profileMap[p.id] = p;
    });
    const metaMap = {};
    (metas || []).forEach((m) => {
      metaMap[m.client_id] = m;
    });

    return (snapshots || [])
      .map((snap) => {
        const profile = profileMap[snap.client_id] || {};
        const meta = metaMap[snap.client_id] || {};
        return {
          id: snap.client_id,
          name: profile.full_name || meta.code || snap.client_id.slice(0, 8),
          email: profile.email || "",
          patrimonio: snap.patrimonio_total || 0,
          period: snap.period || "—",
          code: meta.code || "",
          hasEmail: !!profile.email,
        };
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, []);

  const { data: _clients, loading, error } = useQuery(fetchClients);
  const clients = Array.isArray(_clients) ? _clients : [];

  // ── AUTOMAÇÃO: Auto-selecionar todos os clientes com e-mail ao carregar ──
  useEffect(() => {
    if (clients.length > 0 && selectedClients.size === 0 && sendStatus === STATUS.IDLE) {
      const withEmail = clients.filter((c) => c.hasEmail).map((c) => c.id);
      if (withEmail.length > 0) {
        setSelectedClients(new Set(withEmail));
        setSelectAll(true);
      }
    }
  }, [clients.length]); // only re-run when client count changes

  // ── AUTOMAÇÃO: Carregar histórico de entregas ────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data, error: err } = await supabase
          .from("report_deliveries")
          .select("id, client_id, status, delivered_at, created_at")
          .order("created_at", { ascending: false })
          .limit(50);
        if (!err && data) {
          setDeliveryHistory(data);
        }
      } catch {
        // Table may not exist — graceful
      }
    })();
  }, [sendStatus]);

  const toggleClient = (id) => {
    setSelectedClients((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedClients(new Set());
    } else {
      const withEmail = clients.filter((c) => c.hasEmail).map((c) => c.id);
      setSelectedClients(new Set(withEmail));
    }
    setSelectAll(!selectAll);
  };

  const handleSendBatch = async () => {
    if (selectedClients.size === 0) return;
    setSendStatus(STATUS.SENDING);
    const newResults = [];

    for (const clientId of selectedClients) {
      const client = clients.find((c) => c.id === clientId);
      try {
        await invokeSendMonthlyReport({
          client_id: clientId,
          period: selectedPeriod,
        });
        newResults.push({
          clientId,
          name: client?.name || clientId,
          status: "ok",
          message: "Enviado com sucesso",
        });

        // Record delivery
        supabase
          .from("report_deliveries")
          .insert({
            client_id: clientId,
            status: "delivered",
            delivered_at: new Date().toISOString(),
          })
          .then(() => {})
          .catch(() => {});
      } catch (err) {
        newResults.push({
          clientId,
          name: client?.name || clientId,
          status: "error",
          message: err.message || "Erro desconhecido",
        });

        // Record failure
        supabase
          .from("report_deliveries")
          .insert({
            client_id: clientId,
            status: "failed",
          })
          .then(() => {})
          .catch(() => {});
      }
    }

    setResults(newResults);
    setSendStatus(STATUS.DONE);

    const sent = newResults.filter((r) => r.status === "ok").length;
    const failed = newResults.filter((r) => r.status === "error").length;
    await logAudit("batch_report_sent", {
      period: selectedPeriod,
      total: newResults.length,
      sent,
      failed,
    }).catch(() => {});
  };

  // ── AUTOMAÇÃO: Retry falhas ──────────────────────────────────────────────
  const handleRetryFailed = useCallback(async () => {
    const failedResults = results.filter((r) => r.status === "error");
    if (failedResults.length === 0) return;
    setSendStatus(STATUS.SENDING);

    const retryResults = [];
    for (const fr of failedResults) {
      try {
        await invokeSendMonthlyReport({
          client_id: fr.clientId,
          period: selectedPeriod,
        });
        retryResults.push({ ...fr, status: "ok", message: "Reenviado com sucesso" });
      } catch (err) {
        retryResults.push({ ...fr, status: "error", message: err.message || "Retry falhou" });
      }
    }

    // Merge: replace failed with retry results
    const okResults = results.filter((r) => r.status === "ok");
    setResults([...okResults, ...retryResults]);
    setSendStatus(STATUS.DONE);

    const retrySent = retryResults.filter((r) => r.status === "ok").length;
    logAudit("batch_report_retry", {
      period: selectedPeriod,
      retried: retryResults.length,
      sent: retrySent,
    }).catch(() => {});
  }, [results, selectedPeriod]);

  const emailCount = clients.filter((c) => c.hasEmail).length;
  const selectedWithEmail = [...selectedClients].filter((id) => {
    const c = clients.find((cl) => cl.id === id);
    return c?.hasEmail;
  });

  const successCount = results.filter((r) => r.status === "ok").length;
  const errorCount = results.filter((r) => r.status === "error").length;

  // Check last delivery per client
  const lastDeliveryMap = useMemo(() => {
    const map = {};
    for (const d of deliveryHistory) {
      if (!map[d.client_id] && d.status === "delivered") {
        map[d.client_id] = d.delivered_at;
      }
    }
    return map;
  }, [deliveryHistory]);

  return (
    <div style={{ padding: 0 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h3 style={{ margin: 0, color: T.text, fontSize: 18, fontWeight: 700 }}>
            📧 Envio de Relatórios Mensais
          </h3>
          <p style={{ margin: "4px 0 0", color: T.muted, fontSize: 13 }}>
            Clientes com e-mail são auto-selecionados · Entregas rastreadas automaticamente
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <label style={{ color: T.muted, fontSize: 13, fontWeight: 600 }}>Período:</label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            style={{
              padding: "6px 12px",
              borderRadius: RADIUS.sm,
              border: `1px solid ${T.border}`,
              background: T.bg,
              color: T.text,
              fontSize: 13,
            }}
          >
            {periodOptions.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats cards */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          { label: "Total Clientes", value: clients.length, color: T.text },
          { label: "Com E-mail", value: emailCount, color: "#16A34A" },
          { label: "Selecionados", value: selectedWithEmail.length, color: "#2563EB" },
          {
            label: "Já Receberam",
            value: Object.keys(lastDeliveryMap).length,
            color: "#7C3AED",
          },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              flex: 1,
              minWidth: 120,
              padding: "12px 16px",
              background: T.card,
              borderRadius: RADIUS.md,
              border: `1px solid ${T.border}`,
            }}
          >
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginBottom: 4 }}>
              {s.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Client list */}
      {loading ? (
        <SkeletonTable rows={5} cols={5} />
      ) : error ? (
        <p style={{ color: "#DC2626" }}>Erro: {error.message}</p>
      ) : (
        <>
          {/* Select all + Send button */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                style={{ width: 16, height: 16 }}
              />
              <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>
                Selecionar todos com e-mail ({emailCount})
              </span>
            </label>
            <button
              onClick={handleSendBatch}
              disabled={sendStatus === STATUS.SENDING || selectedWithEmail.length === 0}
              style={{
                padding: "10px 24px",
                borderRadius: RADIUS.md,
                border: "none",
                background: sendStatus === STATUS.SENDING ? "#94A3B8" : "#16A34A",
                color: "#fff",
                fontWeight: 800,
                fontSize: 14,
                cursor: sendStatus === STATUS.SENDING ? "wait" : "pointer",
                opacity: selectedWithEmail.length === 0 ? 0.5 : 1,
              }}
            >
              {sendStatus === STATUS.SENDING
                ? "⏳ Enviando..."
                : `📧 Enviar para ${selectedWithEmail.length} clientes`}
            </button>
          </div>

          {/* Table */}
          <div
            style={{
              border: `1px solid ${T.border}`,
              borderRadius: RADIUS.md,
              overflow: "hidden",
              maxHeight: 440,
              overflowY: "auto",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: T.card, position: "sticky", top: 0, zIndex: 1 }}>
                  <th style={{ width: 40, padding: "10px 8px" }} />
                  <th
                    style={{
                      textAlign: "left",
                      padding: "10px 12px",
                      fontSize: 11,
                      fontWeight: 700,
                      color: T.muted,
                    }}
                  >
                    CLIENTE
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "10px 12px",
                      fontSize: 11,
                      fontWeight: 700,
                      color: T.muted,
                    }}
                  >
                    E-MAIL
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      padding: "10px 12px",
                      fontSize: 11,
                      fontWeight: 700,
                      color: T.muted,
                    }}
                  >
                    PATRIMÔNIO
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: "10px 12px",
                      fontSize: 11,
                      fontWeight: 700,
                      color: T.muted,
                    }}
                  >
                    ÚLTIMO SNAP
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: "10px 12px",
                      fontSize: 11,
                      fontWeight: 700,
                      color: T.muted,
                    }}
                  >
                    ÚLTIMO ENVIO
                  </th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => {
                  const lastDel = lastDeliveryMap[c.id];
                  return (
                    <tr
                      key={c.id}
                      style={{
                        borderBottom: `1px solid ${T.border}`,
                        background: selectedClients.has(c.id)
                          ? "rgba(37,99,235,0.06)"
                          : "transparent",
                        opacity: c.hasEmail ? 1 : 0.5,
                      }}
                    >
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={selectedClients.has(c.id)}
                          onChange={() => toggleClient(c.id)}
                          disabled={!c.hasEmail}
                          style={{ width: 16, height: 16 }}
                        />
                      </td>
                      <td
                        style={{
                          padding: "8px 12px",
                          fontSize: 13,
                          fontWeight: 600,
                          color: T.text,
                        }}
                      >
                        {c.name}
                        {c.code && (
                          <span style={{ color: T.muted, fontSize: 11, marginLeft: 6 }}>
                            ({c.code})
                          </span>
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px 12px",
                          fontSize: 12,
                          color: c.hasEmail ? T.text : "#DC2626",
                        }}
                      >
                        {c.email || "Sem e-mail"}
                      </td>
                      <td
                        style={{
                          padding: "8px 12px",
                          fontSize: 13,
                          fontWeight: 600,
                          color: T.text,
                          textAlign: "right",
                        }}
                      >
                        {fmt(c.patrimonio)}
                      </td>
                      <td
                        style={{
                          padding: "8px 12px",
                          fontSize: 12,
                          color: T.muted,
                          textAlign: "center",
                        }}
                      >
                        {c.period}
                      </td>
                      <td
                        style={{
                          padding: "8px 12px",
                          fontSize: 11,
                          color: lastDel ? "#16A34A" : T.muted,
                          textAlign: "center",
                          fontWeight: lastDel ? 600 : 400,
                        }}
                      >
                        {lastDel
                          ? new Date(lastDel).toLocaleDateString("pt-BR", {
                              day: "2-digit",
                              month: "short",
                            })
                          : "–"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Results */}
      {sendStatus === STATUS.DONE && results.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              display: "flex",
              gap: 12,
              marginBottom: 12,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div
              style={{
                padding: "10px 16px",
                borderRadius: RADIUS.sm,
                background: "#F0FDF4",
                border: "1px solid #BBF7D0",
                fontSize: 13,
                fontWeight: 700,
                color: "#16A34A",
              }}
            >
              ✅ Sucesso: {successCount}
            </div>
            {errorCount > 0 && (
              <>
                <div
                  style={{
                    padding: "10px 16px",
                    borderRadius: RADIUS.sm,
                    background: "#FEF2F2",
                    border: "1px solid #FECACA",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#DC2626",
                  }}
                >
                  ❌ Erros: {errorCount}
                </div>
                <button
                  onClick={handleRetryFailed}
                  style={{
                    padding: "8px 16px",
                    borderRadius: RADIUS.sm,
                    border: "none",
                    background: "#D97706",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  🔄 Retry {errorCount} falha(s)
                </button>
              </>
            )}
          </div>

          {errorCount > 0 && (
            <div
              style={{
                background: T.card,
                borderRadius: RADIUS.md,
                padding: 16,
                border: `1px solid ${T.border}`,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 8 }}>
                Detalhes dos erros:
              </div>
              {results
                .filter((r) => r.status === "error")
                .map((r) => (
                  <div
                    key={r.clientId}
                    style={{
                      fontSize: 12,
                      color: "#DC2626",
                      padding: "4px 0",
                      borderBottom: `1px solid ${T.border}`,
                    }}
                  >
                    <strong>{r.name}:</strong> {r.message}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Schedule Configuration */}
      <div style={{ marginTop: 24 }}>
        <ReportScheduleConfig T={T} />
      </div>
    </div>
  );
}

AdminReportSendTab.propTypes = {
  T: PropTypes.object.isRequired,
};
